import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/api'
import { useSocketStore } from '@/stores/socket'
import { useAuthStore } from '@/stores/auth'

export const useChatStore = defineStore('chat', () => {
  const chats = ref([])
  const currentChat = ref(null)
  const messages = ref([])
  const loading = ref(false)
  const unreadCount = ref(0)
  const typingUsers = ref(new Map())
  const showChatModal = ref(false)
  const authStore = useAuthStore()

  const getAuthUserId = () => authStore.user?.id || null

  const recalculateUnreadCount = () => {
    unreadCount.value = chats.value.reduce((total, chat) => total + (chat.unreadCount || 0), 0)
  }

  // Computed
  const sortedChats = computed(() => {
    const socketStore = useSocketStore()
    const sorted = [...chats.value]

    return sorted.sort((a, b) => {
      // First priority: Real chats (with messages) come first
      if (a.lastMessage && !b.lastMessage) return -1
      if (!a.lastMessage && b.lastMessage) return 1
      
      // Both have messages - sort by timestamp
      if (a.lastMessage && b.lastMessage) {
        return new Date(b.lastMessage.createdAt) - new Date(a.lastMessage.createdAt)
      }
      
      // Both virtual chats - prioritize online users
      const aOnline = socketStore.onlineUsers.has(a.user.id)
      const bOnline = socketStore.onlineUsers.has(b.user.id)
      
      if (aOnline && !bOnline) return -1
      if (!aOnline && bOnline) return 1
      
      // Same status - sort by name
      return a.user.fio.localeCompare(b.user.fio)
    })
  })

  const totalUnreadCount = computed(() => {
    return chats.value.reduce((total, chat) => total + chat.unreadCount, 0)
  })

  // Actions
  const loadChats = async () => {
    loading.value = true
    try {
      // Выполняем запросы параллельно
      const [chatsResult, unreadCountResult] = await Promise.allSettled([
        api.get('/messages/chats'),
        api.get('/messages/unread-count')
      ])
      
      if (chatsResult.status === 'fulfilled') {
        chats.value = Array.isArray(chatsResult.value.data)
          ? chatsResult.value.data.map(chat => ({
              ...chat,
              unreadCount: chat.unreadCount || 0
            }))
          : []
      }

      if (unreadCountResult.status === 'fulfilled') {
        unreadCount.value = unreadCountResult.value.data?.unreadCount ?? 0
      }
      recalculateUnreadCount()
    } catch (error) {
      // Console log removed
    } finally {
      loading.value = false
    }
  }

  const loadMessages = async (userId, page = 1) => {
    loading.value = true
    try {
      const response = await api.get(`/messages/chat/${userId}?page=${page}`)
      
      if (page === 1) {
        messages.value = response.data.messages
      } else {
        messages.value = [...response.data.messages, ...messages.value]
      }
      
      // Mark messages as read асинхронно (не блокируем UI)
      const authUserId = getAuthUserId()
      const unreadMessages = authUserId
        ? response.data.messages
            .filter(msg => !msg.isRead && msg.receiverId === authUserId)
            .map(msg => msg.id)
        : []
      
      if (unreadMessages.length > 0) {
        // Запускаем в фоне, не ждем завершения
        markMessagesAsRead(unreadMessages).catch(error => {
          // Console log removed
        })
      }
      
      return response.data
    } catch (error) {
      // Console log removed
      throw error
    } finally {
      loading.value = false
    }
  }

  const sendMessage = async (receiverId, message, messageType = 'TEXT', fileUrl = null) => {
    const socketStore = useSocketStore()
    
    try {
      if (socketStore.connected) {
        // Send via socket for real-time delivery
        const messageData = {
          receiverId,
          message,
          messageType,
          fileUrl
        }
        
        socketStore.sendMessage(messageData)
        
        // Note: The actual message object will be received via socket events
        // and added to the chat in the socket handlers
        
        return { success: true }
      } else {
        // Fallback to HTTP API if socket is not connected
        const response = await api.post('/messages/send', {
          receiverId,
          message,
          messageType,
          fileUrl
        })
        
        // Add message to current chat if it's the active chat
        if (currentChat.value?.user.id === receiverId) {
          messages.value.push(response.data.data)
        }
        
        // Update chat list
        await updateChatWithNewMessage(response.data.data)
        
        return response.data.data
      }
    } catch (error) {
      // Console log removed
      throw error
    }
  }

  const markMessagesAsRead = async (messageIds) => {
    try {
      await api.put('/messages/mark-read', { messageIds })

      const socketStore = useSocketStore()
      if (socketStore.connected) {
        socketStore.markAsRead(messageIds)
      }
      
      // Update local messages
      messages.value.forEach(msg => {
        if (messageIds.includes(msg.id)) {
          msg.isRead = true
        }
      })
      
      // Update chat unread count
      const authUserId = getAuthUserId()
      if (currentChat.value && authUserId) {
        const chat = chats.value.find(c => c.user.id === currentChat.value.user.id)
        if (chat) {
          // Only decrease when these messages were addressed to current user
          const ownMessagesRead = messages.value.filter(msg => messageIds.includes(msg.id) && msg.receiverId === authUserId)
          chat.unreadCount = Math.max(0, chat.unreadCount - ownMessagesRead.length)
        }
      }
      recalculateUnreadCount()
      await updateUnreadCount()
    } catch (error) {
      // Console log removed
    }
  }

  const markMessageAsRead = (messageId) => {
    // Find and update the specific message
    const message = messages.value.find(msg => msg.id === messageId)
    if (message) {
      message.isRead = true
    }
  }

  const updateUnreadCount = async () => {
    // Debounce to prevent too many API calls
    if (updateUnreadCount.timeout) {
      clearTimeout(updateUnreadCount.timeout)
    }
    
    updateUnreadCount.timeout = setTimeout(async () => {
      try {
        const response = await api.get('/messages/unread-count')
        const serverCount = response.data?.unreadCount ?? 0
        unreadCount.value = Math.max(unreadCount.value, serverCount)
      } catch (error) {
        // Console log removed
      }
    }, 500) // Wait 500ms before making the request
  }

  const updateChatWithNewMessage = async (message) => {
    const authUserId = getAuthUserId()
    if (!authUserId) {
      return
    }

    const otherUserId = message.senderId === authUserId
      ? message.receiverId
      : message.senderId
    
    let chat = chats.value.find(c => c.user.id === otherUserId)
    
    if (!chat) {
      const isIncoming = message.receiverId === authUserId
      const isActiveChat = currentChat.value?.user.id === otherUserId
      // Создаем временный чат сразу, загрузку пользователя делаем в фоне
      chat = {
        user: { id: otherUserId, fio: 'Загрузка...', role: '' },
        lastMessage: message,
        unreadCount: isIncoming && !isActiveChat ? 1 : 0
      }
      chats.value.push(chat)
      
      // Загружаем данные пользователя в фоне
      api.get(`/users/${otherUserId}`)
        .then(userResponse => {
          chat.user = userResponse.data
        })
        .catch(error => {
          // Console log removed
          chat.user.fio = 'Ошибка загрузки'
        })
    } else {
      // Update existing chat
      chat.lastMessage = message
      const isIncoming = message.receiverId === authUserId
      const isActiveChat = currentChat.value?.user.id === otherUserId
      if (isIncoming && !isActiveChat) {
        chat.unreadCount = (chat.unreadCount || 0) + 1
      }
    }
    recalculateUnreadCount()
    // Обновление счетчика тоже делаем в фоне
    updateUnreadCount()
  }

  const addNewMessage = (message) => {
    // Add to messages if it's for the current chat
    if (currentChat.value && 
        (message.senderId === currentChat.value.user.id || 
         message.receiverId === currentChat.value.user.id)) {
      messages.value.push(message)
    }
    
    // Update chat list
    updateChatWithNewMessage(message)
  }

  const setCurrentChat = (user) => {
    currentChat.value = { user }
    messages.value = []

    const chat = chats.value.find(c => c.user.id === user.id)
    if (chat) {
      chat.unreadCount = 0
    }
    recalculateUnreadCount()
  }

  const clearCurrentChat = () => {
    currentChat.value = null
    messages.value = []
  }

  const setTyping = (userId, isTyping) => {
    if (isTyping) {
      typingUsers.value.set(userId, true)
    } else {
      typingUsers.value.delete(userId)
    }
  }

  const isUserTyping = (userId) => {
    return typingUsers.value.has(userId)
  }

  const getUserChats = async () => {
    try {
      const response = await api.get('/messages/users')
      return response.data
    } catch (error) {
      // Console log removed
      return []
    }
  }

  const deleteMessage = async (messageId) => {
    try {
      await api.delete(`/messages/${messageId}`)
      messages.value = messages.value.filter(msg => msg.id !== messageId)
    } catch (error) {
      // Console log removed
      throw error
    }
  }

  const searchUsers = async (query = '', role = '') => {
    try {
      const params = new URLSearchParams()
      if (query) params.append('search', query)
      if (role) params.append('role', role)
      
      const response = await api.get(`/messages/users?${params.toString()}`)
      return response.data
    } catch (error) {
      // Console log removed
      throw error
    }
  }

  const startNewChat = async (userId) => {
    try {
      // Find if chat already exists
      const existingChat = chats.value.find(chat => chat.user.id === userId)
      if (existingChat) {
        setCurrentChat(existingChat)
        return existingChat
      }

      // Get user details to create new chat
      const response = await api.get(`/users/${userId}`)
      const user = response.data
      
      if (!user) {
        throw new Error('Пользователь не найден')
      }

      // Create new chat object
      const newChat = {
        user,
        lastMessage: null,
        unreadCount: 0
      }

      chats.value.unshift(newChat)
      setCurrentChat(newChat)
      return newChat
    } catch (error) {
      // Console log removed
      throw error
    }
  }

  const uploadAttachment = async (file) => {
    const formData = new FormData()
    formData.append('file', file)

    const response = await api.post('/messages/upload', formData)
    return response.data.data
  }

  return {
    // State
    chats,
    currentChat,
    messages,
    loading,
    unreadCount,
    typingUsers,
    showChatModal,
    
    // Computed
    sortedChats,
    totalUnreadCount,
    
    // Actions
    loadChats,
    loadMessages,
    sendMessage,
    markMessagesAsRead,
    markMessageAsRead,
    updateUnreadCount,
    addNewMessage,
    setCurrentChat,
    clearCurrentChat,
    setTyping,
    isUserTyping,
    getUserChats,
    deleteMessage,
    searchUsers,
    startNewChat,
    uploadAttachment
  }
})
